import React, { ChangeEventHandler, CSSProperties, forwardRef, InputHTMLAttributes, useEffect, useRef } from 'react';

import clsx from 'clsx';
import { createUseStyles } from 'react-jss';

import Text, { HeaderFontFeatureSettings } from 'DesignSystem2/Text';
import Padding from 'DesignSystem2/Theme/Padding';

import { Border, Breakpoint, Palette, Radius, Spacing } from '../Theme';

const NumberFontFeatureSettings = "'tnum' on, 'lnum' on, 'case' on, 'cv01' on, 'cv08' on, 'cv09'";

const useStyles = createUseStyles({
    container: {
        padding: [Spacing(1.5), Spacing(2)],
        width: 'calc(100% + 2px)',
        display: 'block',
        boxSizing: 'border-box',
        textAlign: 'start',
        border: Border(Palette.Fill.x40),
        borderRadius: Radius.x30,
    },
    containerEnabled: {
        composes: '$container',
        cursor: 'text',
        borderRadius: Radius.x30,
        boxSizing: 'border-box',
        outline: '0px solid transparent',
        border: Border(Palette.Fill.x40),
        '&:hover': {
            border: Border(Palette.Fill.x30),
        },
        '&:focus-within': {
            border: Border('#00D991', false),
            outline: '1px solid #00D991',
        },
    },
    containerEnabledRow: {
        composes: '$containerEnabled',
        '&:hover': {
            borderRadius: Radius.x30,
        },
        '&:focus-within': {
            borderRadius: Radius.x30,
        },
    },
    containerEnabledBorderDisabled: {
        composes: '$container',
        cursor: 'text',
        borderColor: 'transparent',
    },
    containerDisabled: {
        composes: '$container',
        backgroundColor: Palette.Fill.x50,
    },
    errorSquare: {
        display: 'inline-block',
        borderRadius: Radius.x10,
        height: 8,
        width: 8,
        marginRight: Spacing(1),
        backgroundColor: Palette.Critical.x10,
    },
    input: {
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: NumberFontFeatureSettings,
        fontSize: '15px',
        lineHeight: '24px',
        fontWeight: 500,
        border: 0,
        outline: 0,
        padding: 0,
        backgroundColor: 'transparent',
        width: '100%',
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: NumberFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
        // Hack to avoid mobile browser zooming in on input focus: https://defensivecss.dev/tip/input-zoom-safari/
        [Breakpoint.down.sm]: {
            fontSize: '16px',
        },
    },
    largeInput: {
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: NumberFontFeatureSettings,
        fontSize: '32px',
        lineHeight: '40px',
        fontWeight: 600,
        letterSpacing: '4px',
        border: 0,
        borderRadius: Radius.x10,
        outline: 0,
        padding: 0,
        backgroundColor: 'transparent',
        paddingLeft: 'calc((100% - 145px) / 2)',
        width: '145px',
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: NumberFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
    },
    bigInput: {
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: NumberFontFeatureSettings,
        fontSize: '30px',
        lineHeight: '40px',
        fontWeight: 600,
        letterSpacing: '-0.6px',
        border: 0,
        borderRadius: Radius.x10,
        outline: 0,
        padding: 0,
        backgroundColor: 'transparent',
        width: '145px',
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: NumberFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
    },
    smallInput: {
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: NumberFontFeatureSettings,
        fontSize: '12px',
        lineHeight: '18px',
        fontWeight: 400,
        border: 0,
        outline: 0,
        padding: 0,
        backgroundColor: 'transparent',
        width: '100%',
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: NumberFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
    },
    ideasInput: {
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: HeaderFontFeatureSettings,
        fontSize: '20px',
        lineHeight: '30px',
        fontWeight: 400,
        border: 0,
        outline: 0,
        padding: 0,
        backgroundColor: 'transparent',
        width: '100%',
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: HeaderFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
    },
    ideasReplyInput: {
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: HeaderFontFeatureSettings,
        fontSize: '16px',
        lineHeight: '24px',
        height: '24px',
        fontWeight: 400,
        border: 0,
        outline: 0,
        padding: 0,
        backgroundColor: 'transparent',
        width: '100%',
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: HeaderFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
    },
    inputEnabled: {
        composes: '$input',
        color: Palette.Text.x10,
    },
    inputDisabled: {
        composes: '$input',
        color: Palette.Text.x20,
        cursor: 'not-allowed',
    },
    rowInput: {
        textAlign: 'right',
        display: 'block',
    },
});

interface BaseTextInputProps extends InputHTMLAttributes<HTMLInputElement> {
    labelWidget?: JSX.Element;
    labelText?: string;
    labelTextColor?: string;
    labelIcon?: JSX.Element;
    trailingAction?: JSX.Element;
    row?: boolean;
    rows?: number;
    autoFocus?: boolean;
    largeText?: boolean;
    bigText?: boolean;
    smallText?: boolean;
    ideasInput?: boolean;
    ideasReplyInput?: boolean;
    borderDisabled?: boolean;
    multilineOnChange?: ChangeEventHandler<HTMLTextAreaElement>;
    labelStyles?: CSSProperties;
    componentBelow?: JSX.Element;
    onFocusCallback?: () => void;
    errorText?: string;
    warningText?: string;
    helperText?: string;
    helperTextColor?: string;
    onKeyDownTextArea?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const TextInput = forwardRef(
    (
        {
            labelWidget = undefined,
            labelText = '',
            labelTextColor = undefined,
            labelIcon = undefined,
            trailingAction = undefined,
            errorText = '',
            warningText = '',
            helperText = '',
            helperTextColor = Palette.Text.x20,
            className,
            row = false,
            largeText = false,
            bigText = false,
            smallText = false,
            ideasInput = false,
            ideasReplyInput = false,
            borderDisabled = false,
            autoFocus = false,
            multilineOnChange = undefined,
            rows = undefined,
            componentBelow,
            labelStyles,
            onFocusCallback,
            onKeyDownTextArea,
            ...inputProps
        }: BaseTextInputProps,
        ref: React.Ref<HTMLInputElement>,
    ): JSX.Element => {
        const classes = useStyles();
        const { disabled } = inputProps;

        const textareaRef = useRef<HTMLTextAreaElement>(null);

        useEffect(() => {
            if (textareaRef !== null && textareaRef.current !== null) {
                textareaRef.current.style.height = '0px';
                const scrollHeight = textareaRef.current.scrollHeight;
                textareaRef.current.style.height = scrollHeight + 'px';
            }
        }, [inputProps.value]);

        const label = !!labelWidget ? (
            <div className={'flex'}>{labelWidget}</div>
        ) : (
            !!labelText && (
                <div className={'flex'}>
                    {!!labelIcon && labelIcon}
                    {!!labelIcon && <Padding spacing={0.5} />}
                    <Text.Body.x20
                        color={!!labelTextColor ? labelTextColor : disabled ? Palette.Text.x20 : Palette.Text.x10}
                    >
                        {labelText}
                    </Text.Body.x20>
                </div>
            )
        );
        const helper = !!helperText && <Text.Body.x20 color={helperTextColor}>{helperText}</Text.Body.x20>;
        const warning = !!warningText && <Text.Body.x20 color={Palette.Orange.x10}>{warningText}</Text.Body.x20>;
        const error = !!errorText && (
            <span>
                <span className={classes.errorSquare} />
                <Text.Body.x20 color={Palette.Critical.x10}>{errorText}</Text.Body.x20>
            </span>
        );

        const inputId = inputProps.id ?? Math.random().toString();
        const input = multilineOnChange ? (
            <textarea
                ref={textareaRef}
                id={inputId}
                value={inputProps.value}
                onChange={multilineOnChange}
                className={clsx(
                    largeText
                        ? classes.largeInput
                        : bigText
                        ? classes.bigInput
                        : smallText
                        ? classes.smallInput
                        : ideasInput
                        ? classes.ideasInput
                        : ideasReplyInput
                        ? classes.ideasReplyInput
                        : classes.input,
                    disabled ? classes.inputDisabled : classes.inputEnabled,
                    row && classes.rowInput,
                )}
                placeholder={inputProps.placeholder}
                rows={rows}
                style={{ resize: 'none', width: '100%', height: '100%', boxSizing: 'border-box' }}
                autoFocus={autoFocus}
                onFocus={() => {
                    if (onFocusCallback) {
                        onFocusCallback();
                    }
                }}
                onKeyDown={onKeyDownTextArea}
            />
        ) : (
            <input
                type={'text'}
                className={clsx(
                    largeText ? classes.largeInput : bigText ? classes.bigInput : classes.input,
                    disabled ? classes.inputDisabled : classes.inputEnabled,
                    ideasInput && classes.ideasInput,
                    row && classes.rowInput,
                )}
                disabled={disabled}
                ref={ref}
                id={inputId}
                autoFocus={autoFocus}
                {...inputProps}
            />
        );

        const labeledInput = row ? (
            <span className={'inline-flex items-center justify-between w-full'}>
                {!!label && (
                    <div className={'flex flex-col w-full'}>
                        <span className={'block flex-shrink-0 pr-1'}>{label}</span>
                        {componentBelow}
                    </div>
                )}
                {input}
            </span>
        ) : (
            <div className={'flex'}>
                <div className={'flex flex-col w-full'}>
                    {!!label && (
                        <span style={{ display: 'block', marginBottom: Spacing(0.25) }} className={'opacity-50'}>
                            {label}
                        </span>
                    )}
                    {input}
                </div>
                {!!trailingAction && trailingAction}
            </div>
        );

        return (
            <label
                className={clsx(
                    className,
                    'transition-all duration-300 max-w-full',
                    disabled
                        ? classes.containerDisabled
                        : borderDisabled
                        ? classes.containerEnabledBorderDisabled
                        : row
                        ? classes.containerEnabledRow
                        : classes.containerEnabled,
                )}
                htmlFor={inputId}
                style={labelStyles}
            >
                {labeledInput}
                {!!helper && <span className={'block mt-0.25'}>{helper}</span>}
                {(!!warning || !!error) && (
                    <span className={'block mt-0.25'}>
                        {warning} {error}
                    </span>
                )}
            </label>
        );
    },
);

TextInput.displayName = 'TextInput';
export default TextInput;
